/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from "react";
import ct from "class-types.macro";
import { z } from "zod";
import { SubmitHandler } from "react-hook-form";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";
import Input from "~/common/Form/Input";
import Form from "~/common/Form";
import SelectInput from "~/common/Form/SelectInput";
const schema = z.object({
  meatType: z.string().nonempty(""),
  weightPerDay: z.string().nonempty("Weight Per Day (lb.) must be a number").pipe(z.coerce.number({
    message: "Weight Per Day (lb.) must be a number."
  }).positive("").gt(0)),
  pricePerPound: z.string().nonempty("Price Per Pound must be a number").pipe(z.coerce.number({
    message: "Price Per Pound must be a number."
  }).positive("").gt(0))
});
interface Props {
  price: number;
}
type FormFields = z.infer<typeof schema>;
const HoldOvenCalculator = ({
  price
}: Props) => {
  const [increasedYield, setIncreasedYield] = useState("");
  const [paysForSelf, setPaysForSelf] = useState("");
  const [savingsPerWeek, setSavingsPerWeek] = useState("");
  const [savingsPerMonth, setSavingsPerMonth] = useState("");
  const [savingsPerYear, setSavingsPerYear] = useState("");
  const meatTypes = [{
    text: "Chicken",
    value: "1",
    percentSavings: 0.23
  }, {
    text: "Prime Rib",
    value: "2",
    percentSavings: 0.25
  }, {
    text: "Top/Bottom Round",
    value: "3",
    percentSavings: 0.21
  }, {
    text: "Brisket",
    value: "4",
    percentSavings: 0.15
  }, {
    text: "Pork Roast",
    value: "5",
    percentSavings: 0.15
  }, {
    text: "Turkey",
    value: "6",
    percentSavings: 0.18
  }, {
    text: "Lamb",
    value: "7",
    percentSavings: 0.15
  }];
  const handleReset = (event: React.SyntheticEvent) => {
    event.preventDefault();
    setSavingsPerMonth("");
    setSavingsPerWeek("");
    setSavingsPerYear("");
    setIncreasedYield("");
    setPaysForSelf("");
  };
  const handleSubmit: SubmitHandler<FormFields> = data => {
    const weightPerDayNumber = data.weightPerDay;
    const pricePerLbNumber = data.pricePerPound;
    const selectedMeatType = meatTypes.find(x => x.value === data.meatType);
    const savingsPerDay = (selectedMeatType?.percentSavings || 0) * weightPerDayNumber * pricePerLbNumber;
    // Set Increase Yield
    setIncreasedYield(`${((selectedMeatType?.percentSavings || 0) * 100).toFixed(0)}%`);
    // Pays for self
    setPaysForSelf(`${(price / (savingsPerDay * 30)).toFixed(1)} Months`);
    // Savings Per Week
    setSavingsPerWeek(`${formatAsCurrency.formatIfUndefined(savingsPerDay * 7)}`);
    // Savings Per Month
    setSavingsPerMonth(`${formatAsCurrency.formatIfUndefined(savingsPerDay * 30)}`);
    // Savings Per Year
    setSavingsPerYear(`${formatAsCurrency.formatIfUndefined(savingsPerDay * 365.2)}`);
  };
  const outputStyles: React.CSSProperties = {
    borderRadius: "4px",
    boxShadow: "inset 0 1px 4px rgb(0 0 0 / 4%), 0 1px 0 #fff",
    border: "1px solid rgba(0,0,0,.12)",
    background: "rgba(0,0,0,.04)"
  };
  return <div style={{
    borderRadius: "4px",
    boxShadow: "0 1px 4px rgb(0 0 0 / 8%), 0 1px 0 white inset"
  }} className={ct("bg-gray-200", "border-gray-200", "border-solid", "border", "leading-6", "mx-auto", "mt-5", "mb-10", "text-left", "w-4/5", "xxl:w-11/12")} data-sentry-component="HoldOvenCalculator" data-sentry-source-file="HoldOvenCalculator.tsx">
      <div className={ct("border-gray-200", "border-solid", "border", "border-t-0", "border-r-0", "border-l-0", "border-b", "py-3", "px-5")}>
        <h4 style={{
        textShadow: "0 1px 0 #fff"
      }} className={ct("text-xl", "leading-6", "m-0")}>
          Generated Savings Calculator
        </h4>
      </div>
      <Form<FormFields> validationSchema={schema} onSubmit={handleSubmit} data-sentry-element="Form" data-sentry-source-file="HoldOvenCalculator.tsx">
        <div style={{
        textShadow: "0 1px 0 white"
      }} className={ct("text-xl", "px-5", "py-4-1/2")}>
          <div data-testid="input-container">
            <SelectInput name="meatType" options={meatTypes} label="Meat Type" placeholder="Select an option" labelClassName={ct("font-semibold", "mb-2", "text-gray-800")} data-testid="hold-oven-meat-type-select" data-sentry-element="SelectInput" data-sentry-source-file="HoldOvenCalculator.tsx" />
            <Input type="text" label="Weight Per Day (lb.)" placeholder="Enter Weight" name="weightPerDay" labelClassName={ct("font-semibold", "mb-2", "text-gray-800")} data-testid="hold-oven-weight-per-day-input" defaultValues="" data-sentry-element="Input" data-sentry-source-file="HoldOvenCalculator.tsx" />
            <Input type="text" label="Price Per Pound" placeholder="Enter Price" name="pricePerPound" labelClassName={ct("font-semibold", "mb-2", "text-gray-800")} data-testid="hold-oven-price-per-pound-input" defaultValues="" data-sentry-element="Input" data-sentry-source-file="HoldOvenCalculator.tsx" />
          </div>

          <hr className={ct("mt-4", "mb-8")} />

          <div style={outputStyles} className={ct("py-3", "px-4")}>
            <div style={{
            borderBottom: "1px solid rgba(0,0,0,.08)"
          }} className={ct("font-semibold", "text-base", "mb-1", "pb-1", "text-gray-800")}>
              Increased Yield
            </div>
            <div data-testid="hold-oven-increased-yield-output" className={ct("font-semibold", "text-2xl", "text-gray-900")}>
              {increasedYield}
            </div>
          </div>

          <div style={outputStyles} className={ct("mt-8", "py-3", "px-4")}>
            <div style={{
            borderBottom: "1px solid rgba(0,0,0,.08)"
          }} className={ct("font-semibold", "text-base", "mb-1", "pb-1", "text-gray-800")}>
              Pays For Self
            </div>
            <div data-testid="hold-oven-pays-for-self-output" className={ct("font-semibold", "text-2xl", "text-gray-900")}>
              {paysForSelf}
            </div>
          </div>

          <div style={outputStyles} className={ct("mt-8", "py-3", "px-4")}>
            <div style={{
            borderBottom: "1px solid rgba(0,0,0,.08)"
          }} className={ct("font-semibold", "text-base", "mb-1", "pb-1", "text-gray-800")}>
              Savings Per Week
            </div>
            <div data-testid="hold-oven-savings-week-output" className={ct("font-semibold", "text-2xl", "text-gray-900")}>
              {savingsPerWeek}
            </div>
          </div>

          <div style={outputStyles} className={ct("mt-8", "py-3", "px-4")}>
            <div style={{
            borderBottom: "1px solid rgba(0,0,0,.08)"
          }} className={ct("font-semibold", "text-base", "mb-1", "pb-1", "text-gray-800")}>
              Savings Per Month
            </div>
            <div data-testid="hold-oven-savings-month-output" className={ct("font-semibold", "text-2xl", "text-gray-900")}>
              {savingsPerMonth}
            </div>
          </div>

          <div style={outputStyles} className={ct("mt-8", "py-3", "px-4")}>
            <div style={{
            borderBottom: "1px solid rgba(0,0,0,.08)"
          }} className={ct("font-semibold", "text-base", "mb-1", "pb-1", "text-gray-800")}>
              Savings Per Year
            </div>
            <div data-testid="hold-oven-savings-year-output" className={ct("font-semibold", "text-2xl", "text-gray-900")}>
              {savingsPerYear}
            </div>
          </div>
        </div>
        <div className={ct("border-gray-200", "border-solid", "border", "border-r-0", "border-b-0", "border-l-0", "border-t", "px-5", "py-2-1/2")}>
          <button type="submit" className="btn btn-primary" data-testid="hold-oven-calculate-button">
            Calculate
          </button>
          <a href="" className={ct("float-right", "text-base", "py-2")} style={{
          textShadow: "0 1px 0 #fff"
        }} onClick={handleReset} data-testid="hold-oven-reset-button">
            Reset
          </a>
        </div>
      </Form>
    </div>;
};
export default HoldOvenCalculator;